import Chart from "chart.js/auto";
import { CONTENT_WIDTH, MARGIN_LEFT, bottomMarginItemGlobal } from "../../utils/pdfConstants";

const INTEREST_RATE = 0.04;
const PAYMENTS_PER_YEAR = 12;

const GREEN = "#04aa00";
const WHITE = "white";

function getHuoneistoNeliot(rakennusData, mergedRakennusData) {
  const dataToUse =
    mergedRakennusData?.rakennukset_fulls?.length > 0
      ? mergedRakennusData
      : rakennusData || {};
  const buildings = Array.isArray(dataToUse.rakennukset_fulls)
    ? dataToUse.rakennukset_fulls
    : [];
  const total = buildings
    .filter(r => r.isMainBuilding)
    .reduce((sum, r) => {
      const val = Number(String(r.huoneistoala || "").replace(/\s/g, "").replace(",", "."));
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0);
  return total || 1;
}

function calculateAnnuityFactor(rate, loanYears, ppy) {
  const periodRate = rate / ppy;
  const periods = loanYears * ppy;
  return (periodRate * Math.pow(1 + periodRate, periods)) /
         (Math.pow(1 + periodRate, periods) - 1);
}

function calculateCumulative(perM2Values, factor) {
  let running = 0;
  return perM2Values.map(cost => {
    running += (cost || 0) * factor;
    return running;
  });
}

async function renderLineChart(years, cumulative10, cumulative25) {
  const canvas = document.createElement("canvas");
  canvas.width = 900;
  canvas.height = 380;
  const ctx = canvas.getContext("2d");

  const interestPct = (INTEREST_RATE * 100).toFixed(2);

  const chart = new Chart(ctx, {
    type: "line",
    data: {
      labels: years,
      datasets: [
        {
          label: `Rahoitusvastike / m² 10v lainalla ${interestPct}% korolla`,
          data: cumulative10.map(v => parseFloat(v.toFixed(2))),
          borderColor: "#2F5930",
          backgroundColor: "rgba(47,89,48,0.08)",
          borderWidth: 2.5,
          pointRadius: 3,
          pointBackgroundColor: "#2F5930",
          fill: true,
          tension: 0.3,
        },
        {
          label: `Rahoitusvastike / m² 25v lainalla ${interestPct}% korolla`,
          data: cumulative25.map(v => parseFloat(v.toFixed(2))),
          borderColor: "#7AA668",
          backgroundColor: "rgba(122,166,104,0.08)",
          borderWidth: 2.5,
          pointRadius: 3,
          pointBackgroundColor: "#7AA668",
          fill: true,
          tension: 0.3,
        },
      ],
    },
    options: {
      animation: false,
      responsive: false,
      plugins: {
        title: {
          display: true,
          text: "TEOREETTINEN RAHOITUSVASTIKE",
          color: "#888",
          font: { size: 13, weight: "normal" },
          padding: { bottom: 12 },
        },
        legend: {
          position: "bottom",
          labels: { font: { size: 12 }, boxWidth: 14, padding: 14 },
        },
      },
      scales: {
        x: {
          border: { display: false },
          grid: { display: false },
          ticks: { font: { size: 12 } },
        },
        y: {
          border: { display: false },
          grid: { color: "#eeeeee" },
          ticks: {
            font: { size: 12 },
            callback: v => v.toFixed(2),
          },
        },
      },
    },
  });

  const base64 = canvas.toDataURL("image/png");
  chart.destroy();
  return base64;
}

function buildTalousTable(ptsContext, huoneistoNeliot, cumulative10, cumulative25, contentWidth) {
  const { years, currentYear, footerTotals: totalValues } = ptsContext;
  const fontSize = 8.1;

  const formatVal2 = v => (v != null ? v.toFixed(2) : "-");
  const formatVal1 = v => (v != null ? v.toFixed(1) : "-");

  const perM2Values = (totalValues || []).map(s => (s * 1000) / huoneistoNeliot);
  const totalCost = (totalValues || []).reduce((a, b) => a + (b || 0), 0);
  const kertakustannus = ((totalCost * 1000) / huoneistoNeliot).toFixed(0);
  const last10 = cumulative10[cumulative10.length - 1];
  const last25 = cumulative25[cumulative25.length - 1];
  const lastYear = years[years.length - 1];

  const headerCell = (text, opts = {}) => ({
    text, bold: true, font: "Lato", fontSize,
    color: WHITE, fillColor: GREEN, alignment: "center", ...opts,
  });

  const dataCell = (text, opts = {}) => ({
    text: String(text), font: "Lato", fontSize, alignment: "center", ...opts,
  });

  const numCols = 1 + years.length;

  const VALUE_COLS = Math.min(4, years.length - 1);
  const annuityRow = (label, value) => ([
    headerCell(label, { colSpan: numCols - VALUE_COLS, alignment: "left" }),
    ...Array(numCols - VALUE_COLS - 1).fill({}),
    headerCell(value, { colSpan: VALUE_COLS, alignment: "right" }),
    ...Array(VALUE_COLS - 1).fill({}),
  ]);

  const spacer = () => ([{ text: "", colSpan: numCols, border: [false, false, false, false] }, ...Array(numCols - 1).fill({})]);

  const rows = [
    // Super-header: left label cell + right kustannustaso cell (two separate cells, like yhteenveto)
    [
      headerCell("Kustannusten jakautuminen", { alignment: "left" }),
      headerCell(`Kustannustaso ${currentYear || new Date().getFullYear()} sis. Alv 25,5%`, { colSpan: years.length, alignment: "right" }),
      ...Array(years.length - 1).fill({}),
    ],
    // Year header (green)
    [
      headerCell("", { alignment: "left" }),
      ...years.map(y => headerCell(String(y))),
    ],
    // Yearly totals — white
    [
      dataCell("Kustannukset taloyhtiölle yhteensä vuosittain (x 1000€)", { alignment: "left", fillColor: "#f2f2f2" }),
      ...(totalValues || []).map(v => dataCell(v != null ? String(v) : "-", { fillColor: "#f2f2f2", noWrap: true })),
    ],
    spacer(),
    // €/m² per year — white
    [
      dataCell("Kustannukset vuosittain € / huoneistoneliö", { alignment: "left" }),
      ...perM2Values.map(v => dataCell(formatVal2(v), { noWrap: true })),
    ],
    spacer(),
    // Total cost
    [
      dataCell("Kustannukset yhteensä 10 vuoden jaksolla", { alignment: "left", fillColor: "#f2f2f2" }),
      dataCell(`${(totalCost * 1000).toLocaleString("fi-FI")} €`, { colSpan: years.length, alignment: "left", fillColor: "#f2f2f2" }),
      ...Array(years.length - 1).fill({}),
    ],
    spacer(),
    // Huoneistoala
    [
      dataCell("Kohteen huoneistoala", { alignment: "left" }),
      dataCell(`${huoneistoNeliot} m²`, { colSpan: years.length, alignment: "left" }),
      ...Array(years.length - 1).fill({}),
    ],
    // Kertakustannus
    [
      dataCell("Kertakustannus per huoneistoneliö 10v ajanjaksolla", { alignment: "left", fillColor: "#f2f2f2" }),
      dataCell(`${Number(kertakustannus).toLocaleString("fi-FI")} €/m²`, { colSpan: years.length, alignment: "left", fillColor: "#f2f2f2" }),
      ...Array(years.length - 1).fill({}),
    ],
    spacer(),
    // Teoreettinen rahoitusvastike footer rows (green, at bottom)
    annuityRow(
      `Teoreettinen rahoitusvastike 10v lainalla ja ${(INTEREST_RATE * 100).toFixed(2)}% korolla kuukausittain`,
      `${formatVal1(last10)} €/m²${lastYear ? ` v. ${lastYear}` : ""}`
    ),
    annuityRow(
      `Teoreettinen rahoitusvastike 25v lainalla ja ${(INTEREST_RATE * 100).toFixed(2)}% korolla kuukausittain`,
      `${formatVal1(last25)} €/m²${lastYear ? ` v. ${lastYear}` : ""}`
    ),
  ];

  const labelW = 155;

  return {
    table: {
      widths: [labelW, ...Array(years.length).fill('*')],
      body: rows,
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft:   () => 2,
      paddingRight:  () => 2,
      paddingTop:    () => 3,
      paddingBottom: () => 3,
    },
  };
}

export async function renderTalousBlock({ ptsContext, content, rakennusData, mergedRakennusData, contentWidth = CONTENT_WIDTH }) {
  if (!ptsContext) return;

  const { years, footerTotals: totalValues } = ptsContext;
  if (!years?.length) return;

  const huoneistoNeliot = getHuoneistoNeliot(rakennusData, mergedRakennusData);
  const perM2Values = (totalValues || []).map(s => (s * 1000) / huoneistoNeliot);

  const factor10 = calculateAnnuityFactor(INTEREST_RATE, 10, PAYMENTS_PER_YEAR);
  const factor25 = calculateAnnuityFactor(INTEREST_RATE, 25, PAYMENTS_PER_YEAR);
  const cumulative10 = calculateCumulative(perM2Values, factor10);
  const cumulative25 = calculateCumulative(perM2Values, factor25);

  const chartBase64 = await renderLineChart(years, cumulative10, cumulative25);

  content.push({
    image: chartBase64,
    width: contentWidth,
    margin: [0, 0, 0, bottomMarginItemGlobal],
  });

  content.push({
    stack: [buildTalousTable(ptsContext, huoneistoNeliot, cumulative10, cumulative25, contentWidth)],
    margin: [0, 0, 0, bottomMarginItemGlobal],
  });
}
